import { OrderApiError, OrderMeal, OrderMealEntree, PreConfiguredMeal, RecentOrFavoriteEntree, RecentOrFavoriteOrderMeal } from '@/models';
import { PageParameters } from '../../models';

export type AnalyticsMeal = OrderMeal | OrderMeal[] | RecentOrFavoriteOrderMeal | RecentOrFavoriteOrderMeal[] | PreConfiguredMeal;
export type AnalyticsEntree = OrderMealEntree | RecentOrFavoriteEntree;

export interface AnalyticsDataLayerEvent {
  event?: AnalyticsEventType;
  eventType?: AnalyticsXDMEventType | string;
  web?: AnalyticsWebEvent;
  account?: AccountManagementAnalytics;
  storeLocator?: StoreLocator;
  error?: AnalyticsError;
  commerce?: Commerce;
  productListItems?: Product[];
  marketing?: Marketing;
  targetPageParameters?: PageParameters;
}

export interface AnalyticsWebEvent {
  webPageDetails: WebPageDetails;
  webInteraction?: WebInteraction;
  marketing?: Marketing;
}
export interface ChipotleTenant {
  pas: PasExperienceData
}

export interface PasExperienceData {
  experienceID?: string;
  experienceName?: string; 
  experienceDisplay?: Value;
  experienceInteract?: Value;
  experienceSource?: string;
}

export interface WebPageDetails {
  page?: string;
  pageViews?: Value;
  subPageViews?: Value;
  menuPageViews?: Value;
  name?: string;
  siteSection?: string;
  URL?: string;
  subSection1?: string;
  subSection2?: string;
  siteRegion?: string;
  pageType?: string;
  subPageName?: string;
  language?: string;
  server?: string;
  previousPage?: string;
  errorPage?: string;
  layer?: string;
}

export interface AdobeOrder {
  crewTipAmount?: number;
  currencyCode?: string;
  deliveryFeeAmount?: number;
  donationAmount?: number;
  driverTipAmount?: number;
  groupOrderParticipants?: number;
  orderID?: string;
  paymentMethod?: string;
  pickupTime?: string;
  pointsRedeemed?: number;
  priceBagTotalAmount?: number;
  pricePromoAmount?: number;
  priceSubTotal?: number;
  priceTotal?: number;
  promoCode?: string;
  purchaseID?: string;
  regulatoryFeeAmount?: number;
  serviceFeeAmount?: number;
  taxAmount?: number;
}

export interface WebInteraction {
  linkClicks: Value;
  name?: string;
  type?: string;
  linkModuleName?: string;
  ctaClicks?: Value;
  ctaNameType?: string;
  carouselClicks?: Value;
  carouselNameInteraction?: string;
  socialShareClicks?: Value;
  socialName?: string;
  socialPlatform?: string;
  socialFollowClicks?: Value;
  toolInteractions?: Value;
  toolName?: string;
  linkSection?: string;
  linkPosition?: number | string | null;
  linkCategory?: string;
  linkName?: string;
  linkTitle?: string;
  linkType?: string;
  nutritionData?: string;
}

export interface Marketing {
  trackingCode?: string | (string | null)[];
  respondentID?: string | (string | null)[];
  internalCampaign?: string;
  internalCampaignClicks?: Value;
}

export interface AccountManagementAnalytics {
  [key: string]: Value | string | number | undefined | null | boolean;
  rewardsAddedToBag?: Value;
  logins?: Value;
  logouts?: Value;
  loginStatus?: boolean;
  loginType?: RegistrationType;
  registrationStart?: Value;
  registrationComplete?: Value;
  registrationRewardsStart?: Value;
  registrationType?: RegistrationType;
  requestRewardsStart?: Value;
  requestRewardsComplete?: Value;
  recentOrdersViewed?: Value;
  favoriteOrdersViewed?: Value;
  orderFavorited?: Value;
  accountInteraction?: Value;
  cartAddFavoritedMeal?: Value;
  qrScanned?: Value;
  interactionName?: string;
  favoritedMeal?: boolean;
  userID?: string;
  availablePoints?: number | string;
  rewardsViewed?: Value;
  rewardsAddedToWallet?: Value;
  rewardsFavorited?: Value;
  rewardsScanned?: Value;
  rewardsRedeemed?: Value;
  rewardsID?: string;
  rewardsPath?: string;
  rewardsTitle?: string;
  rewardsType?: string;
  /**
   * @deprecated Use rewardsTitle instead.
   */
  rewardTitle?: string;
  isRewards?: boolean | string;
  accountCountry?: string;
  dietaryPreference?: string;
  dietaryAvoid?: string;
  nutritionPrefs?: Value;
  rewardsEnroll?: Value | null;
  devicePlatform?: string;
  optOutPers?: boolean;
  under18?: boolean;
  ranking?: string;
  needsBirthdayGuac?: boolean;
  loyaltyTier?: string;
  specialLoyaltyStart?: Value;
  specialLoyaltyComplete?: Value;
}

export interface StoreLocator {
  [key: string]: Value | string | number | undefined | null;
  searchInitiated?: Value;
  searchType?: string;
  locationSelected?: Value;
  restaurantFavorited?: Value;
  directionsClicked?: Value;
  pickupLocation?: Value;
  cateringCTA?: Value;
  deliveryAddressLookup?: Value;
  addAddressDetails?: Value;
  deliveryAddressSubmitted?: Value;
  storeId: number | string;
}

export interface Product {
  SKU?: string;
  name?: string;
  priceTotal?: number;
  quantity?: number;
  productAddMethod?: string;
  _merchVars?: MerchVars;
}

export interface MerchVars {
  proteinChoice?: string;
  riceChoice?: string;
  beansChoice?: string;
  topThingsChoice?: string;
  otherOptions?: string;
  includedSides?: string;
  addOns?: string;
  drinks?: string;
  upsellType?: string;
  upsellAmount?: number;
  pceName?: string;
}

export interface Value {
  value: number;
}

export interface Commerce {
  [key: string]: Value | string | undefined | AdobeOrder | Offer;
  changeToDelivery?: Value;
  changeToPickup?: Value;
  groupOrderInitiated?: Value;
  groupOrderCompleted?: Value;
  copiedGroupOrderLink?: Value;
  addAnotherMenuItem?: Value;
  cartEdits?: Value;
  orderDuplicates?: Value;
  orderReview?: Value;
  orderConfirmed?: Value;
  orderSubmitClick?: Value;
  pceMakeItMyOwn?: Value;
  productListViews?: Value;
  productViews?: Value;
  productListAdds?: Value;
  productListRemovals?: Value;
  productListOpens?: Value;
  checkouts?: Value;
  pickupDeliveryDefaultMethod?: string;
  purchases?: Value;
  driverTipCount?: Value;
  crewTipCount?: Value;
  donationCount?: Value;
  order?: AdobeOrder;
  checkoutFunnelInteractions?: Value;
  step1Checkouts?: Value;
  selectionLocation?: Value;
  funnelName?: string;
  retrievalType?: string;
  retrievalTypeSet?: Value;
  upsells?: Value;
  pfm?: ProductFindingMethod;
  checkoutType?: string;
  pceAddToBag?: Value;
  orderReviewTime?: string;
  orderConfirmationMethod?: string;
  minPickupTime?: string;
  driverPickupTime?: string,
  minDeliveryTime?: string,
  maxDeliveryTime?: string,
  localTime?: string;
  sputLinkPosition?: string;
  sputLinkTime?: string;
  menuListViews?: Value;
  cartAddFavoritedMeal?: Value;
  cartAddRecentMeal?: Value;
  upsellAddToBag?: Value;
  upsellCheckout?: Value;
  upsellOrder?: Value;
  upsellImpression?: Value;
  startBuild?: Value;
  startOrder?: Value;
  orderPath?: string;
  orderPathCount?: Value;
  storeSetMethod?: string;
  offer?: Offer;
  alternateLocationImpression?: Value;
  alternateLocationStart?: Value;
  alternationLocationComplete?: Value;
  universalBuilderView?: Value;
  localizedBuilderView?: Value;
}

export interface Offer {
  itemID: string;
  offerActivated?: Value;
  offerName: string;
  offerTitle: string;
  offerType: string;
  offerSource: string;
  rewardStore: OfferRewardStore;
}

export interface OfferRewardStore {
  isActivated: boolean;
  campaignType: string;
}

export enum ProductFindingMethod {
  AddOn = 'addon',
  Upsell = 'upsell',
  Standalone = 'standalone',
}

export interface AnalyticsError {
  errors: Value;
  errorName: string;
  errorMessage?: string;
  errorCode?: string;
  serviceName?: string;
  internalErrorName?: string;
  midType?: string;
  errorType?: string;
  correlationID?: string;
}

export enum AnalyticsEventType {
  PageLoaded = 'pageLoaded',
  LinkClicked = 'linkClicked',
  AddToBag = 'cartAdd',
  RemoveFromBag = 'cartRemove',
  OpenBag = 'cartOpen',
  ViewBag = 'cartView',
  Checkout = 'scCheckout',
  OrderConfirmed = 'orderConfirmed',
  SubmitOrder = 'purchases',
  ProductViews = 'productViews',
  TwoStepVerificationStart = '2svStart',
  TwoStepVerificationComplete = '2svComplete',
  TwoStepVerificationLockout = '2svLockout',
  TwoStepVerificationInvalidMax = '2svInvalidMax',
  TwoStepVerificationInvalid = '2svInvalid',
  TwoStepVerificationResendCode = '2svResendCode',
  TwoStepVerificationGetSupport = '2svGetSupport',
  StatusChanged = 'statusChanged',
  Error = 'error',
  ExperienceRequest = 'experienceRequest',
  PropositionDisplay = 'propositionDisplay',
  LoginComplete = 'loginComplete',
  LogoutComplete = 'logoutComplete',
  Reward = 'reward',
  ActivateOffer = 'cmg_activateOffer'
}

export enum AnalyticsXDMEventType {
  PageLoaded = 'web.webpagedetails.pageViews',
  LinkClicked = 'web.webinteraction.linkClicks',
  AddToBag = 'commerce.productListAdds',
  RemoveFromBag = 'commerce.productListRemovals',
  OpenBag = 'commerce.productListOpens',
  ViewBag = 'commerce.productListViews',
  Checkout = 'commerce.checkouts',
  OrderConfirmed = 'cmg_orderConfirmed',
  SubmitOrder = 'commerce.purchases',
  ProductViews = 'commerce.productViews',
  TwoStepVerificationStart = 'cmg_2svStart',
  TwoStepVerificationComplete = 'cmg_2svComplete',
  TwoStepVerificationLockout = 'cmg_2svLockout',
  TwoStepVerificationInvalidMax = 'cmg_2svInvalidMax',
  TwoStepVerificationInvalid = 'cmg_2svInvalid',
  TwoStepVerificationResendCode = 'cmg_2svResendCode',
  TwoStepVerificationGetSupport = 'cmg_2svGetSupport',
  StatusChanged = 'cmg_2svStatusChanged',
  Error = 'cmg_error',
  ExperienceRequest = 'personalization.request',
  PropositionDisplay = 'decisioning.propositionDisplay',
  LoginComplete = 'cmg_logincomplete',
  LogoutComplete = 'cmg_logoutcomplete',
  Reward = 'cmg_rewards',
  ActivateOffer = 'cmg_activateOffer'
}

export enum RegistrationType {
  OrderWeb = 'orderWeb',
  CateringWeb = 'cateringWeb',
  Automatic = 'automatic',
  Manual = 'manual',
}

export enum InteractionTrackingType {
  GroupOrderInitiated = 'groupOrderInitiated',
  Logins = 'logins',
  OrderNowClicks = 'orderNowClicks',
  OrderReview = 'orderReview',
  OrderConfirmed = 'orderConfirmed',
  scCheckout = 'scCheckout',
  RegistrationComplete = 'registrationComplete',
  RegistrationStart = 'registrationStart',
  RewardsViewed = 'rewards-viewed',
  BagRewardsViewed = 'bag-rewards-viewed',
  RewardsFavorited = 'rewards-favorited',
  RewardsAddedToBag = 'rewards-added-to-bag',
  EditInfo = 'personal-preferences|edit-info',
  UpdatePassword = 'personal-preferences|update-password',
  LeaveRewards = 'personal-preferences|leave-chipotle-rewards',
  JoinRewards = 'personal-preferences|join-chipotle-rewards',
  ToggleEmailOn = 'personal-preferences|toggle-subscriptions|toggle-email-on',
  ToggleEmailOff = 'personal-preferences|toggle-subscriptions|toggle-email-off',
  ToggleTextOn = 'personal-preferences|toggle-subscriptions|toggle-text-on',
  ToggleTextOff = 'personal-preferences|toggle-subscriptions|toggle-text-off',
  SelectNutritionPreferences = 'personal-preferences|select-nutrition-preferences',
  AccessiblePickupToggleOn = 'personal-preferences|accessible-pickup-preference|toggle-on',
  AccessiblePickupToggleOff = 'personal-preferences|accessible-pickup-preference|toggle-off',
  AddCreditCard = 'payment-methods|add-credit-card',
  AddGiftCard = 'payment-methods|add-gift-card',
  RemoveCreditCard = 'payment-methods|remove-credit-card',
  RemoveGiftCard = 'payment-methods|remove-gift-card',
  EditAddress = 'delivery-address|edit-address',
  AddAdressDetails = 'delivery-address|add-address-details',
  PickupInstead = 'delivery-address|pickup-instead',
  ClickFavorites = 'favorite-recent-orders|click-pill-favorites',
  ClickRecents = 'favorite-recent-orders|click-pill-recents',
  OrderDuplications =  'orderDuplications',
}

export enum AutoAccrualTrackingType {
  EnrollInAutoAccrual = 'enroll-in-auto-accrual',
  AutoAccrualInfo = 'auto-accrual-info',
  AutoAccrualOn = 'auto-accrual-on',
  AutoAccrualOff = 'auto-accrual-off',
  AutoAccrualActivated = 'auto-accrual-activated',
  AutoAccrualDeactivated = 'auto-accrual-deactivated',
}

export enum RestaurantSelectionType {
  Favorite = 'Favorite',
  Nearby = 'Nearby',
  Recent = 'Recent',
}

export enum PageLoadEventType {
  VerifyGuacMode = 'get-verified-guacmode',
  GuacModeVerified = 'youre-verified-guacmode',
  UnlockGuacMode = 'unlock-guacmode',
  GuacModeNewAccount = 'guacmode-new-account',
  WelcomeToRewards = 'welcome-to-rewards',
  JoinRewards = 'reward-yourself-free-chipotle',
}

export enum LinkClickElementType {
  Button = 'button',
  Link = 'link'
}

export enum GuacModeLinkClickType {
  VerifyMe = 'verify me',
  LearnMore = 'learn more',
  JoinNow = 'join now',
  HowGuacModeWorks = 'how guacmode works',
  CreateAnAccount = 'create-an-account',
  SignIn = 'sign-in'
}

export enum NutritionLinkClickType {
  MealSelectAllergenStatement = 'meal-select-allergen-statement',
  AllergenStatement = 'allergen-statement',
  FullNutritionFacts = 'full-nutrition-facts',
  NutritionPreferences = 'nutrition-preferences',
  NutritionPreferencesApply = 'nutrition-preferences-apply',
  ViewAllIngredients = 'view-all-ingredients',
  NutritionBreakdown = 'nutrition-breakdown',
  SelectYourMeal = 'select-your-meal'
}

// Used for scenarios where a descriptive string value is 
// wanted in place of null, undefined or empty string.
export enum EmptyValueDescription {
  NotChosenYet = '(not-chosen-yet)',
  NotSet = '(not-set)',
  NotReceived = '(not-received)',
  NotLoggedIn = '(not-logged-in)',
}

export type ApiError = OrderApiError;

export enum AnalyticsMealType {
  Favorite = 'favorite',
  Recent = 'recent',
  Standard = 'standard'
}

export enum AnalyticsUpsellType {
  Static = 'upsell_static',
  Personal = 'upsell_personal',
  NotUpsell = 'not_upsell'
}